import 'filepond/dist/filepond.min.css';

import { Box, Button, CircularProgress, FormControl, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { saveAs } from 'file-saver';
import { ActualFileObject } from 'filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import React, { useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { useTranslation } from 'react-i18next';
import ModalContainer, { create } from 'react-modal-promise';

import ConfirmationDialog from '../../../../../../components/ConfirmationDialog/ConfirmationDialog';
import InputSwitch from '../../../../../../components/Inputs/Switch';
import { SwitchSize } from '../../../../../../components/Inputs/Switch/Switch';
import { api } from '../../../../../../core/api';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch } from '../../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../Context/BenchmarkContext.types';
import { ResultsInputsHookState } from '../useResultsInputs';

registerPlugin(FilePondPluginFileValidateType);

const downloadSvgIcon =
  '<svg width="20px" height="auto" viewBox="-2.4 -2.4 28.80 28.80" xmlns="http://www.w3.org/2000/svg" fill="#000000" transform="matrix(1, 0, 0, 1, 0, 0)rotate(0)" stroke="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.192"></g><g id="SVGRepo_iconCarrier"> <title></title> <g id="Complete"> <g id="download"> <g> <path d="M3,12.3v7a2,2,0,0,0,2,2H19a2,2,0,0,0,2-2v-7" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7759999999999998"></path> <g> <polyline data-name="Right" fill="none" id="Right-2" points="7.9 12.3 12 16.3 16.1 12.3" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7759999999999998"></polyline> <line fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7759999999999998" x1="12" x2="12" y1="2.7" y2="14.2"></line> </g> </g> </g> </g> </g></svg>';

interface ModalProps {
  isOpen: boolean;
  onResolve: (value?: any) => void;
  onReject: (value?: any) => void;
}

interface OwnProps {
  benchmarkId: string;
  resultsInputs: ResultsInputsHookState;
}

const TestedPartyInputs: React.FC<OwnProps> = ({ benchmarkId, resultsInputs }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const dispatchErrors = useErrorsDispatch();
  const dispatchBenchmark = useBenchmarkDispatch();

  const [files, setFiles] = useState(
    resultsInputs.testedPartyFile
      ? ([
          {
            options: {
              // this will trigger FilePond load function
              type: 'local',
            },
          },
        ] as any[])
      : ([] as any[]),
  );
  const [isDownloadingTemplate, setDownloadingTemplate] = useState(false);

  const idleLabel = `${t(
    'benchmark:benchmarkResults.input.dragAndDrop',
  )}<span class="filepond--label-action">${t(
    'benchmark:benchmarkResults.input.dragAndDropBrowse',
  )}</span>`;

  const injectDownloadIcon = (file: ActualFileObject | File) => {
    const wrapper = createFileNameWrapper();
    const existingIcon = document.querySelector('.filepond--download-icon');
    existingIcon?.remove();

    if (wrapper) {
      const downloadIcon = createDownloadIcon();
      wrapper.append(downloadIcon);
      downloadIcon.addEventListener('click', () => {
        saveAs(file, file.name);
      });
    }
  };

  const createDownloadIcon = () => {
    const icon = document.createElement('span');
    icon.className = 'filepond--download-icon';
    icon.title = t('benchmark:benchmarkResults.input.download');
    icon.innerHTML = downloadSvgIcon;
    return icon;
  };

  const createFileNameWrapper = () => {
    const wrapperClassName = 'filepond--file-sub-wrapper';
    const infoElement = document.querySelector('.filepond--file-info');
    const subElement = document.querySelector('.filepond--file-info-sub');
    let wrapper = document.querySelector(`.${wrapperClassName}`);

    if (!wrapper && infoElement && subElement) {
      wrapper = document.createElement('div');
      wrapper.className = wrapperClassName;
      wrapper.append(subElement);
      infoElement.append(wrapper);
    }

    return wrapper;
  };

  const downloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      await api.benchmark.downloadTestedPartyTemplate();
    } catch (e) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error: e },
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const uploadTestedParty = (
    fieldName: string,
    file: ActualFileObject,
    metadata: { [key: string]: any },
    load: (p: string | { [key: string]: any }) => void,
    error: (errorText: string) => void,
  ) => {
    return api.benchmark
      .uploadTestedParty(file, file.name, benchmarkId)
      .then((id) => {
        load(id);
        onFileChange();
        injectDownloadIcon(file);
        resultsInputs.setTestedPartyFile(file as File);
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetTestedParty,
          payload: { uploaded: true },
        });
      })
      .catch((e) => {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error: e, includeDetails: e.data.error === 'UNABLE_TO_PARSE_TESTED_PARTY' },
        });
        error(e);
      });
  };

  const loadTestedParty = (
    source: any,
    load: (file: ActualFileObject | Blob) => void,
    error: (errorText: string) => void,
    progress: any,
    abort: () => void,
  ) => {
    if (resultsInputs.testedPartyFile) {
      load(resultsInputs.testedPartyFile);
      // let filepond component render first and only then inject
      setTimeout(() => {
        injectDownloadIcon(resultsInputs.testedPartyFile!);
      }, 100);
    } else {
      abort();
    }
  };

  const ConfirmModal = ({ isOpen, onResolve, onReject }: ModalProps) => (
    <ConfirmationDialog
      open={isOpen}
      // FilePond handles only resolved promises
      handleClose={() => onResolve(false)}
      handleConfirmOk={() => onResolve(true)}
      text={t('benchmark:benchmarkResults.input.removeTestedPartyConfirm')}
    />
  );

  const promiseModal = create(ConfirmModal);

  const deleteTestedParty = async (
    source: any,
    load: () => void,
    error: (errorText: string) => void,
  ) => {
    try {
      await api.benchmark.deleteTestedParty(benchmarkId);
      onFileChange();
      resultsInputs.setTestedPartyFile();
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetTestedParty,
        payload: { uploaded: false },
      });
      load();
    } catch (e) {
      error('Unable to delete');
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error: e },
      });
    }
  };

  const onFileChange = () => {
    resultsInputs.submitForm(
      resultsInputs.profitLevelIndicator,
      resultsInputs.indicatorType,
      resultsInputs.yearRange,
      resultsInputs.adjustments,
      resultsInputs.fiscalYearAlignment,
      resultsInputs.manufacturerOrDistributor,
    );
  };

  return (
    <Box className={styles.container}>
      <Button
        variant="contained"
        color="primary"
        className={styles.downloadBtn}
        onClick={downloadTemplate}
        disabled={isDownloadingTemplate}
      >
        {isDownloadingTemplate ? (
          <CircularProgress variant="indeterminate" disableShrink color="secondary" size={'2em'} />
        ) : (
          t('benchmark:benchmarkResults.input.downloadTemplate')
        )}
      </Button>
      <FilePond
        files={files}
        onupdatefiles={setFiles}
        maxFiles={1}
        allowReplace={false}
        labelIdle={idleLabel}
        className={styles.fileUpload}
        acceptedFileTypes={[
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/wps-office.xlsx',
        ]}
        beforeRemoveFile={promiseModal}
        // using custom server functions because our httpClient has required authentication mechanism
        server={{
          process: uploadTestedParty,
          load: loadTestedParty,
          remove: deleteTestedParty,
          revert: deleteTestedParty,
        }}
      />
      <ModalContainer />
      {resultsInputs.testedPartyFile && (
        <FormControl component="fieldset" className={styles.control}>
          <Box mb={4}>
            <Grid container direction="row">
              <Grid item className={styles.gridText}>
                <Typography>
                  {t('benchmark:benchmarkResults.adjustments.fiscalYearAlignment')}
                </Typography>
              </Grid>
              <Grid item>
                <InputSwitch
                  fieldName=""
                  checked={resultsInputs.fiscalYearAlignment}
                  onChange={(value) => resultsInputs.handleFiscalAlignmentChange(value)}
                  size={SwitchSize.small}
                />
              </Grid>
            </Grid>
          </Box>
        </FormControl>
      )}
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    height: '100%',
    flexWrap: 'wrap',

    '& .filepond--wrapper': {
      width: '40%',
    },
  },
  downloadBtn: {
    textTransform: 'none',
    width: '40%',
    height: '2.5em',
  },
  fileUpload: {
    '& .filepond--file-sub-wrapper': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .filepond--download-icon': {
      cursor: 'pointer',
      pointerEvents: 'auto',
      display: 'inline-flex',
      marginLeft: '.5em',
    },
  },
  control: {
    padding: '12px',
  },
  gridText: {
    display: 'flex',
    alignItems: 'center',
  },
}));

export default TestedPartyInputs;
